import { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { formatDateTime } from '@/lib/format/datetime';
import { RunErrorText, RunStatusBadge } from './RunStatus';
import { isRunSettled } from '@/lib/community-curation/isRunSettled';
import { reasonLabelKey } from '@/lib/community-curation/reasonLabelKey';
import { youtubeThumbnailUrl, youtubeWatchUrl } from '@/lib/community-curation/youtubeLinks';
import type {
  CurationPick, CurationPickStatus, CurationRun,
} from '@/lib/community-curation/types';
import {
  ADMIN_COMMUNITY_CURATION_PICKS, ADMIN_COMMUNITY_CURATION_RUN,
} from '@/queries/communityCuration';

const POLL_MS = 3000;
const PICK_LIMIT = 100;
const STATUSES: CurationPickStatus[] = ['added', 'dry_run', 'skipped', 'failed'];

const STATUS_VARIANT: Record<CurationPickStatus, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  added: 'default',
  dry_run: 'secondary',
  skipped: 'outline',
  failed: 'destructive',
};

interface RunPicksDialogProps {
  runId: string | null;
  onOpenChange: (open: boolean) => void;
}

export function RunPicksDialog({ runId, onOpenChange }: RunPicksDialogProps) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<'all' | CurationPickStatus>('all');

  useEffect(() => { setStatus('all'); }, [runId]);

  const runQuery = useQuery(ADMIN_COMMUNITY_CURATION_RUN, {
    variables: { id: runId },
    skip: !runId,
  });
  const run: CurationRun | null = runQuery.data?.adminCommunityCurationRun ?? null;
  const settled = isRunSettled(run);
  // Poll only while the run is still going.
  const { startPolling, stopPolling } = runQuery;
  useEffect(() => {
    if (!runId || settled) {
      stopPolling();
      return undefined;
    }
    startPolling(POLL_MS);
    return () => stopPolling();
  }, [runId, settled, startPolling, stopPolling]);

  // Picks are written at the end of a run, so they are refetched when it
  // settles.
  const picksQuery = useQuery(ADMIN_COMMUNITY_CURATION_PICKS, {
    variables: {
      runId, status: status === 'all' ? null : status, limit: PICK_LIMIT, offset: 0,
    },
    skip: !runId,
  });
  const { refetch: refetchPicks } = picksQuery;
  useEffect(() => {
    if (settled) refetchPicks();
  }, [settled, refetchPicks]);

  const picks: CurationPick[] = picksQuery.data?.adminCommunityCurationPicks?.data ?? [];
  const totalPicks: number = picksQuery.data?.adminCommunityCurationPicks?.total ?? 0;
  const loadError = runQuery.error || picksQuery.error;

  const noteLabel = (note: string | null) => {
    if (note === 'quota_budget_exhausted') return t('admin.communityCuration.runDialog.noteQuotaBudget');
    if (note === 'youtube_quota_exceeded') return t('admin.communityCuration.runDialog.noteYoutubeQuota');
    return note;
  };

  return (
    <Dialog open={!!runId} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle>
            {t('admin.communityCuration.runDialog.title', {
              date: run?.started_at ? formatDateTime(run.started_at) : '',
            })}
          </DialogTitle>
          <DialogDescription>
            {run && (
              <span className="flex flex-wrap items-center gap-2">
                <Badge variant="outline">{t(`admin.communityCuration.triggers.${run.trigger}`)}</Badge>
                {run.dry_run && <Badge variant="secondary">{t('admin.communityCuration.runs.dryRun')}</Badge>}
                <RunStatusBadge status={run.status} />
                <span>
                  {t('admin.communityCuration.runs.result', {
                    added: run.added_count, skipped: run.skipped_count, failed: run.failed_count,
                  })}
                </span>
                <span>{t('admin.communityCuration.runDialog.units', { count: run.youtube_units_used })}</span>
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {loadError && (
          <div className="space-y-2 text-center">
            <p className="text-destructive">{t('admin.communityCuration.runDialog.loadError')}</p>
            <Button size="sm" variant="secondary" onClick={() => { runQuery.refetch(); refetchPicks(); }}>
              {t('admin.communityCuration.retry')}
            </Button>
          </div>
        )}

        {run && <RunErrorText run={run} className="text-sm" />}

        {run && run.niche_results.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-medium">{t('admin.communityCuration.runDialog.niches')}</h3>
            <ul className="grid gap-2 sm:grid-cols-2">
              {run.niche_results.map((result) => (
                <li key={result.niche_id} className="rounded-md border p-2 text-sm">
                  <p className="font-medium">{result.name}</p>
                  <p className="text-muted-foreground">
                    {t('admin.communityCuration.runDialog.nicheSummary', {
                      searches: result.searches, candidates: result.candidates, added: result.added,
                    })}
                  </p>
                  {result.note && <p className="text-amber-500">{noteLabel(result.note)}</p>}
                  {result.errors.map((message) => (
                    <p key={message} className="break-words text-xs text-destructive">{message}</p>
                  ))}
                </li>
              ))}
            </ul>
          </div>
        )}

        {!settled && !loadError && (
          <p className="text-sm text-muted-foreground">{t('admin.communityCuration.running')}</p>
        )}

        <div className="flex items-center justify-between gap-2">
          <p className="text-sm text-muted-foreground">
            {t('admin.communityCuration.runDialog.showing', { shown: picks.length, total: totalPicks })}
          </p>
          <Select value={status} onValueChange={(value) => setStatus(value as typeof status)}>
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('admin.communityCuration.runDialog.filterAll')}</SelectItem>
              {STATUSES.map((value) => (
                <SelectItem key={value} value={value}>
                  {t(`admin.communityCuration.pickStatuses.${value}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('admin.communityCuration.runDialog.columns.video')}</TableHead>
              <TableHead>{t('admin.communityCuration.runDialog.columns.niche')}</TableHead>
              <TableHead className="text-right">{t('admin.communityCuration.runDialog.columns.views')}</TableHead>
              <TableHead className="text-right">{t('admin.communityCuration.runDialog.columns.velocity')}</TableHead>
              <TableHead>{t('admin.communityCuration.runDialog.columns.status')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {settled && !picksQuery.loading && picks.length === 0 && !loadError && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground">
                  {t('admin.communityCuration.runDialog.empty')}
                </TableCell>
              </TableRow>
            )}
            {picks.map((pick) => (
              <TableRow key={pick.id}>
                <TableCell className="max-w-[360px]">
                  <div className="flex gap-3">
                    <a
                      href={youtubeWatchUrl(pick.video_id)}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="block shrink-0"
                    >
                      <img
                        src={youtubeThumbnailUrl(pick.video_id)}
                        alt=""
                        loading="lazy"
                        className="h-12 w-20 rounded object-cover"
                      />
                    </a>
                    <div className="min-w-0 flex-1">
                      <a
                        href={youtubeWatchUrl(pick.video_id)}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="inline-flex max-w-full items-center gap-1 font-medium hover:underline"
                      >
                        <span className="truncate">{pick.title || pick.video_id}</span>
                        <ExternalLink className="h-3 w-3 shrink-0" />
                      </a>
                      <p className="truncate text-xs text-muted-foreground">{pick.channel_title}</p>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-sm">{pick.niche?.name || '—'}</TableCell>
                <TableCell className="text-right text-sm tabular-nums">
                  {pick.view_count != null ? pick.view_count.toLocaleString() : '—'}
                </TableCell>
                <TableCell className="text-right text-sm tabular-nums">
                  {pick.velocity != null
                    ? t('admin.communityCuration.runDialog.velocity', {
                      value: Math.round(pick.velocity).toLocaleString(),
                    })
                    : '—'}
                </TableCell>
                <TableCell className="space-y-1">
                  <Badge variant={STATUS_VARIANT[pick.status]}>
                    {t(`admin.communityCuration.pickStatuses.${pick.status}`)}
                  </Badge>
                  {pick.reason && (
                    <p className="text-xs text-muted-foreground">{t(reasonLabelKey(pick.reason))}</p>
                  )}
                  {pick.error && <p className="max-w-[220px] break-words text-xs text-destructive">{pick.error}</p>}
                  {pick.library?.curation_status && (
                    <p className="text-xs text-muted-foreground">
                      {t('admin.communityCuration.runDialog.libraryStatus', {
                        status: pick.library.curation_status,
                      })}
                    </p>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
    </Dialog>
  );
}
